import React, { useState } from 'react';
import {
  Box,
  Button,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  Stop as StopIcon,
  Flag as FlagIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { 
  addExecutionToTrade, 
  addAdjustmentToTrade,
  selectExecutionsLoading,
  selectTradeState,
  fetchTradeState
} from './executionsSlice';
import { getTrade } from './tradesSlice';
import * as executionsAPI from '../../api/executions';
import DWLFLoader from '../../components/DWLFLoader';

const ExecutionsQuickActions = ({ tradeId, currentPrice = null }) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectExecutionsLoading);
  const tradeState = useSelector(state => selectTradeState(state, tradeId));
  
  // Dialog states
  const [showExecutionDialog, setShowExecutionDialog] = useState(false);
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  
  // Form states
  const [executionForm, setExecutionForm] = useState({
    type: 'entry',
    qty: '',
    price: '',
    fee: '0',
    memo: '',
    source: 'manual',
    ts: new Date().toISOString().slice(0, 16) // Default to current datetime for datetime-local input
  });
  
  const [adjustmentForm, setAdjustmentForm] = useState({
    field: 'stop',
    new: '',
    reason: '',
    ts: new Date().toISOString().slice(0, 16) // Default to current datetime for datetime-local input
  });

  const handleExecutionSubmit = async () => {
    // For fee executions, qty and price are not required
    if (executionForm.type !== 'fee' && (!executionForm.qty || !executionForm.price)) return;
    
    try {
      await dispatch(addExecutionToTrade({
        tradeId,
        executionData: {
          ...executionForm,
          qty: executionForm.type === 'fee' ? undefined : Number(executionForm.qty),
          price: executionForm.type === 'fee' ? undefined : Number(executionForm.price),
          fee: Number(executionForm.fee),
          ts: new Date(executionForm.ts).toISOString() // Convert to ISO string for backend
        }
      })).unwrap();
      
      setShowExecutionDialog(false);
      setExecutionForm({
        type: 'entry',
        qty: '',
        price: '',
        fee: '0',
        memo: '',
        source: 'manual',
        ts: new Date().toISOString().slice(0, 16)
      });
      
      // Refresh trade state and main trade data
      dispatch(fetchTradeState({ tradeId, mark: currentPrice }));
      dispatch(getTrade(tradeId));
    } catch (error) {
      console.error('Failed to add execution:', error);
    }
  };

  const handleAdjustmentSubmit = async () => {
    if (!adjustmentForm.new) return;
    
    try {
      await dispatch(addAdjustmentToTrade({
        tradeId,
        adjustmentData: {
          ...adjustmentForm,
          new: adjustmentForm.field === 'stop' || adjustmentForm.field === 'takeProfit' 
            ? Number(adjustmentForm.new) 
            : adjustmentForm.new,
          ts: new Date(adjustmentForm.ts).toISOString() // Convert to ISO string for backend
        }
      })).unwrap();
      
      setShowAdjustmentDialog(false);
      setAdjustmentForm({
        field: 'stop',
        new: '',
        reason: '',
        ts: new Date().toISOString().slice(0, 16)
      });
      
      // Refresh trade state and main trade data
      dispatch(fetchTradeState({ tradeId, mark: currentPrice }));
      dispatch(getTrade(tradeId));
    } catch (error) {
      console.error('Failed to add adjustment:', error);
    }
  };



  const handleCheckAndClose = async () => {
    try {
      console.log('Checking and closing trade:', tradeId);
      const result = await executionsAPI.checkAndCloseTrade(tradeId);
      console.log('Check-close result:', result);
      
      // Add a small delay to ensure database update is complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh trade state and main trade data
      dispatch(fetchTradeState({ tradeId, mark: currentPrice }));
      dispatch(getTrade(tradeId));
      
      console.log('Full result object:', JSON.stringify(result, null, 2));
      console.log('Result shouldAutoClose:', result.shouldAutoClose, 'Type:', typeof result.shouldAutoClose);
      console.log('Result tradeState shouldAutoClose:', result.tradeState?.shouldAutoClose, 'Type:', typeof result.tradeState?.shouldAutoClose);
      
      const shouldAutoClose = result.shouldAutoClose || result.tradeState?.shouldAutoClose;
      console.log('Final shouldAutoClose value:', shouldAutoClose, 'Type:', typeof shouldAutoClose);
      
      if (shouldAutoClose) {
        console.log('Trade auto-closed successfully');
        // You could add a toast notification here
        alert('Trade has been automatically closed!');
      } else {
        console.log('Trade close check completed');
        alert('Trade close check completed. Trade is already closed or position is not zero.');
      }
    } catch (error) {
      console.error('Failed to check and close trade:', error);
    }
  };

  const getExecutionTypeIcon = (type) => {
    switch (type) {
      case 'entry':
      case 'add':
        return <AddIcon />;
      case 'reduce':
      case 'exit':
        return <RemoveIcon />;
      case 'stop_hit':
      case 'tp_hit':
        return <CloseIcon />;
      default:
        return <AddIcon />;
    }
  };



  const getAdjustmentFieldIcon = (field) => {
    switch (field) {
      case 'stop':
        return <StopIcon />;
      case 'takeProfit':
        return <TrendingUpIcon />;
      case 'label':
      case 'tag':
        return <FlagIcon />;
      default:
        return <EditIcon />;
    }
  };

  const speedDialActions = [
    {
      icon: <AddIcon />,
      name: 'Add Execution',
      action: () => setShowExecutionDialog(true)
    },
    {
      icon: <EditIcon />,
      name: 'Adjust Trade',
      action: () => setShowAdjustmentDialog(true)
    }
  ];

  return (
    <Box>
      {/* Quick Action Buttons */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<AddIcon />}
          onClick={() => {
            setExecutionForm(prev => ({ ...prev, type: 'add' }));
            setShowExecutionDialog(true);
          }}
          disabled={loading}
        >
          Add Position
        </Button>
        
        <Button
          variant="contained"
          color="error"
          startIcon={<RemoveIcon />}
          onClick={() => {
            setExecutionForm(prev => ({ ...prev, type: 'exit' }));
            setShowExecutionDialog(true);
          }}
          disabled={loading}
        >
          Partial Exit
        </Button>
        
        <Button
          variant="outlined"
          color="warning"
          startIcon={<EditIcon />}
          onClick={() => {
            setAdjustmentForm(prev => ({ ...prev, field: 'stop' }));
            setShowAdjustmentDialog(true);
          }}
          disabled={loading}
        >
          Move Stop
        </Button>
        
        <Button
          variant="outlined"
          color="info"
          startIcon={<TrendingUpIcon />}
          onClick={() => {
            setAdjustmentForm(prev => ({ ...prev, field: 'takeProfit' }));
            setShowAdjustmentDialog(true);
          }}
          disabled={loading}
        >
          Set TP
        </Button>
      </Box>

      {/* Current Position Summary */}
      {tradeState?.positionState && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Current Position
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Chip 
                label={`${tradeState.positionState.qty} shares`}
                color="primary"
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Chip 
                label={`$${tradeState.positionState.avgEntry?.toFixed(2) || '0.00'}`}
                color="info"
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Chip 
                label={`$${tradeState.positionState.realizedPnl?.toFixed(2) || '0.00'}`}
                color={tradeState.positionState.realizedPnl >= 0 ? 'success' : 'error'}
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Chip 
                label={`${tradeState.positionState.realizedR?.toFixed(2) || '0.00'}R`}
                color="secondary"
                variant="outlined"
              />
            </Grid>
          </Grid>
          

          
          {/* Show check-close button if position is 0 but trade might still be open */}
          {tradeState.positionState.qty === 0 && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color="info"
                onClick={handleCheckAndClose}
                disabled={loading}
                size="small"
              >
                Check & Close Trade
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Auto-close if position is 0
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Speed Dial for Mobile */}
      <SpeedDial
        ariaLabel="Quick Actions"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        icon={<SpeedDialIcon />}
      >
        {speedDialActions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            onClick={action.action}
          />
        ))}
      </SpeedDial>

      {/* Add Execution Dialog */}
      <Dialog 
        open={showExecutionDialog} 
        onClose={() => setShowExecutionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Execution</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Execution Type</InputLabel>
              <Select
                value={executionForm.type}
                onChange={(e) => setExecutionForm(prev => ({ ...prev, type: e.target.value }))}
                startAdornment={getExecutionTypeIcon(executionForm.type)}
              >
                <MenuItem value="entry">Entry</MenuItem>
                <MenuItem value="add">Add Position</MenuItem>
                <MenuItem value="reduce">Reduce Position</MenuItem>
                <MenuItem value="exit">Exit</MenuItem>
                <MenuItem value="stop_hit">Stop Hit</MenuItem>
                <MenuItem value="tp_hit">Take Profit Hit</MenuItem>
                <MenuItem value="fee">Fee Only</MenuItem>
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={executionForm.qty}
                  onChange={(e) => setExecutionForm(prev => ({ ...prev, qty: e.target.value }))}
                  disabled={executionForm.type === 'fee'}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  step="0.01"
                  value={executionForm.price}
                  onChange={(e) => setExecutionForm(prev => ({ ...prev, price: e.target.value }))}
                  disabled={executionForm.type === 'fee'}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Fee"
              type="number"
              step="0.01"
              value={executionForm.fee}
              onChange={(e) => setExecutionForm(prev => ({ ...prev, fee: e.target.value }))}
              sx={{ mt: 2 }}
            />

            <TextField
              fullWidth
              label="Memo"
              value={executionForm.memo}
              onChange={(e) => setExecutionForm(prev => ({ ...prev, memo: e.target.value }))}
              sx={{ mt: 2 }}
            />

            <TextField
              fullWidth
              label="Date & Time"
              type="datetime-local"
              value={executionForm.ts}
              onChange={(e) => setExecutionForm(prev => ({ ...prev, ts: e.target.value }))}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExecutionDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleExecutionSubmit}
            variant="contained"
            disabled={loading || (executionForm.type !== 'fee' && (!executionForm.qty || !executionForm.price))}
          >
            {loading ? <DWLFLoader size="tiny" showText={false} /> : 'Add Execution'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Adjustment Dialog */}
      <Dialog 
        open={showAdjustmentDialog} 
        onClose={() => setShowAdjustmentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adjust Trade</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Adjustment Type</InputLabel>
              <Select
                value={adjustmentForm.field}
                onChange={(e) => setAdjustmentForm(prev => ({ ...prev, field: e.target.value }))}
                startAdornment={getAdjustmentFieldIcon(adjustmentForm.field)}
              >
                <MenuItem value="stop">Stop Loss</MenuItem>
                <MenuItem value="takeProfit">Take Profit</MenuItem>
                <MenuItem value="label">Label</MenuItem>
                <MenuItem value="tag">Tag</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={adjustmentForm.field === 'stop' || adjustmentForm.field === 'takeProfit' ? 'New Price' : 'New Value'}
              type={adjustmentForm.field === 'stop' || adjustmentForm.field === 'takeProfit' ? 'number' : 'text'}
              step={adjustmentForm.field === 'stop' || adjustmentForm.field === 'takeProfit' ? '0.01' : undefined}
              value={adjustmentForm.new}
              onChange={(e) => setAdjustmentForm(prev => ({ ...prev, new: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Reason"
              value={adjustmentForm.reason}
              onChange={(e) => setAdjustmentForm(prev => ({ ...prev, reason: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Date & Time"
              type="datetime-local"
              value={adjustmentForm.ts}
              onChange={(e) => setAdjustmentForm(prev => ({ ...prev, ts: e.target.value }))}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAdjustmentDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAdjustmentSubmit}
            variant="contained"
            disabled={loading || !adjustmentForm.new}
          >
            {loading ? <DWLFLoader size="tiny" showText={false} /> : 'Add Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExecutionsQuickActions;
